/**
 * Authentication utility for Gynzy.
 *
 * This module provides support for logging in using the gynzy/frontend-accounts-nuxt service using these steps:
 *
 * 1. After a related interaction, the user is redirected to the Gynzy accounts service.
 * 2. The user logs in and is redirected back to the application with a cookie containing the access and refresh tokens.
 * 3. The application extracts the tokens from the cookie, stores them in session storage and afterwards removes the cookie.
 * 4. The application sets a timer to refresh the access token before it expires, using the refresh token.
 *
 * @module gynzyAuthProvider
 * @example
 * // Checking for the presence of tokens in a cookie or in session storage, and refreshing the access token if necessary:
 * import { gynzyAuthProvider } from './utils/auth';
 * ...
 * gynzyAuthProvider.check();
 *
 * @note This module shares some code with and derives inspiration from the gynzy/manager gynzyAuthProvider
 * found in src/providers/gynzy-auth/provider.ts.
 */

import GraphQLClient from '@/utils/graphql-client';
import { getAuthCookie, invalidateAuthCookie } from '../../utils/cookie-util';
import { TokenNotFoundError } from './TokenNotFoundError';
import { TokenRefreshError } from './TokenRefreshError';

// #region Constants
/**
 * Key used to store and retrieve the access token in session storage.
 * @type {string}
 * @constant
 */
const tokenKey = 'auth_token';

/**
 * Key used to store and retrieve the refresh token in session storage.
 * @type {string}
 * @constant
 */
const refreshTokenKey = 'auth_refresh_token';
// #endregion

// #region State

/**
 * Current user object, initialized as null.
 * This will be populated with user data after a successful authentication check.
 * @type {Object|null}
 */
let currentUser;

// #endregion

// #region Token handling functions
/**
 * Parses a JWT token from a string and returns its payload as an object.
 * @param {string} token - The JWT access token to parse.
 * @returns {Object|null} The parsed JWT token as an object, or null if the token is invalid or not provided.
 */
function _parseJwt(token) {
	if (!token) {
		return null;
	}

	const base64Url = token.split('.')[1];
	if (!base64Url) {
		return null;
	}

	const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
	const jsonPayload = decodeURIComponent(
		window
			.atob(base64)
			.split('')
			.map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
			.join('')
	);

	return JSON.parse(jsonPayload);
}

/**
 * Retrieves the access token from session storage, parses it, and returns the parsed token.
 * @returns {Object|null} The parsed access token or null if not found or invalid.
 */
function _getAccessTokenFromStorage({ parseToken } = { parseToken: false }) {
	const token = sessionStorage.getItem(tokenKey);
	return parseToken ? _parseJwt(token) : token;
}

/**
 * Retrieves the refresh token from session storage.
 * @returns {string|null} The refresh token or null if not found.
 */
function _getRefreshTokenFromStorage() {
	return sessionStorage.getItem(refreshTokenKey);
}

/**
 * Checks the presence of an auth cookie, extracts the access and refresh tokens from it,
 * and stores them in session storage. It also removes the cookie after extraction.
 * @returns {Object} An object containing the access token and refresh token extracted from the cookie.
 */
function _getTokensFromCookie() {
	let token;
	let refreshToken;
	const authCookie = getAuthCookie();

	if (authCookie) {
		// Extract the tokens from the cookie.
		token = authCookie.access_token;
		refreshToken = authCookie.refresh_token;
		sessionStorage.setItem(tokenKey, token);
		sessionStorage.setItem(refreshTokenKey, refreshToken);

		// Parse the JWT token to prepare it for use.
		token = _parseJwt(token);

		// Remove the cookie after extracting the tokens so it won't be sent along in additional requests on the domain.
		invalidateAuthCookie();
	}

	return {
		token,
		refreshToken,
	};
}

/**
 * Refresh the access token using the refresh token and set them in storage.
 * @returns {void}
 */
async function _refreshAccessToken(schoolId = null) {
	const refreshToken = _getRefreshTokenFromStorage();
	if (!refreshToken) {
		throw new TokenNotFoundError('No refresh token found');
	}

	const params = new URLSearchParams();
	params.append('grant_type', 'refresh_token');
	params.append('refresh_token', refreshToken);

	const url = new URL('/oauth/token', process.env.GATSBY_ACCOUNTS_API_URL);
	if (schoolId !== null) {
		url.searchParams.append('school_id', schoolId);
	}

	const response = await fetch(url.toString(), {
		method: 'POST',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
		body: params.toString(),
	});

	const data = await response.json();
	if (!data.access_token || !data.refresh_token) {
		throw new TokenRefreshError(`Failed to refresh access token: ${JSON.stringify(data)}`);
	}

	sessionStorage.setItem(tokenKey, data.access_token);
	sessionStorage.setItem(refreshTokenKey, data.refresh_token);

	if (data.access_token && data.refresh_token) {
		// eslint-disable-next-line no-use-before-define
		_startTokenRefreshTimer();
	}
}

/**
 * Removes the access and refresh tokens from session storage.
 * This effectively logs out the user by clearing their authentication state.
 * @returns {void}
 */
function _removeTokensFromStorage() {
	sessionStorage.removeItem(tokenKey);
	sessionStorage.removeItem(refreshTokenKey);
}

// Variables that holds the timer. Scoped outside of the function to allow for
// subsequent calls to the function without creating multiple timers.
let tokenRefreshTimer;

/**
 * Checks if a token refresh timer is already running. If not, it calculates the time until the access token expires
 * and sets a timer to refresh the access token before it expires.
 * The timer is set to refresh the token 30 minutes before it expires..
 * @returns {void}
 */
function _startTokenRefreshTimer() {
	if (tokenRefreshTimer) return;

	const token = _getAccessTokenFromStorage({ parseToken: true });
	const minute = 60 * 1000;
	const gracePeriod = 30 * minute; // 30 minutes in milliseconds
	const timerDuration = token.exp * 1000 - Date.now() - gracePeriod;

	tokenRefreshTimer = setTimeout(async () => {
		tokenRefreshTimer = null; // Reset the timer after refreshing

		try {
			await _refreshAccessToken();
		} catch (e) {
			if (e instanceof TokenNotFoundError) {
				console.error('Token not found:', e.message);
				_removeTokensFromStorage();
				tokenRefreshTimer = null; // Reset the timer after refreshing
			} else if (e instanceof TypeError || e instanceof TokenRefreshError) {
				if (token.exp * 1000 > Date.now() + minute) {
					console.warn('Failed to refresh access token:', e.message, '\n\nTrying again in 1 minute.');
					setTimeout(() => {
						_startTokenRefreshTimer();
					}, minute);
				} else {
					console.error('Failed to refresh access token:', e);
					_removeTokensFromStorage();
				}
			} else {
				console.error('An unexpected error occurred while refreshing the access token:', e);
				_removeTokensFromStorage();
			}
		}
	}, timerDuration);
}
// #endregion

// #region Backend interaction functions

async function _getCurrentUser() {
	const graphqlClient = new GraphQLClient(process.env.GATSBY_GATEWAY_GRAPHQL_URL, {
		Authorization: `Bearer ${_getAccessTokenFromStorage()}`,
		'x-ratelimit-id': _getAccessTokenFromStorage({ parseToken: true })['ratelimit-id'],
	});

	const result = await graphqlClient.postQuery(`
		query {
			currentUser {
				roles {
					type
					schoolId
				}
				schools {
					id
					nationalIdentifier
					name
					address {
						addressLine1
						addressLine2
						zipCode
						city
					}
				}
			}
		}
	`);

	currentUser = result.data.currentUser;
}

// #endregion

// #region Exported Auth Provider
/**
 * Authentication provider.
 * This provider provides mechanisms to check if the user is authenticated,
 * and to log out the user by clearing the tokens from the session storage.
 * @typedef {Object} gynzyAuthProvider
 * @property {Function} check - Checks if the user is authenticated.
 * @property {Function} logout - Logs out the user by removing the access and refresh tokens from session storage.
 */
export const gynzyAuthProvider = {
	/**
	 * Checks if the user is authenticated by looking for tokens in cookies or session storage.
	 * If tokens are found, it starts a timer to refresh the access token before it expires.
	 * @returns {{authenticated: boolean, error?: {message: string, name: string}}}
	 */
	check: () => {
		let { token, refreshToken } = _getTokensFromCookie();

		if (!token || !refreshToken) {
			token = _getAccessTokenFromStorage();
			refreshToken = _getRefreshTokenFromStorage();
		}

		if (!token || !refreshToken) {
			return {
				authenticated: false,
				error: {
					message: 'Check failed',
					name: token ? 'Refresh token not found' : 'Token not found',
				},
			};
		}

		_startTokenRefreshTimer();

		return {
			authenticated: true,
		};
	},
	/**
	 * Logs out the user by removing the access and refresh tokens from session storage.
	 * @returns {void}
	 */
	logout: () => {
		_removeTokensFromStorage();
	},
	isLoggedIn: () => !!_getAccessTokenFromStorage(),
	getAccessToken: ({ parseToken } = { parseToken: false }) => _getAccessTokenFromStorage({ parseToken }),
	/**
	 * Fetches a new token based on the provided school ID.
	 * @returns {Promise<boolean>} Returns true if the token was successfully refreshed, false otherwise.
	 */
	refreshTokenForSchool: async schoolId => {
		try {
			await _refreshAccessToken(schoolId);
			return true;
		} catch (e) {
			console.error('Failed to refresh token for school:', e);
			return false;
		}
	},
	getCurrentUser: async () => {
		if (!currentUser) {
			await _getCurrentUser();
		}

		return currentUser;
	},
};
